import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, Chip, Alert, MenuItem, Tooltip, List, ListItem, ListItemButton, ListItemText, ListItemIcon, IconButton, Snackbar, TablePagination, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Premium Icons
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import DashboardIcon from '@mui/icons-material/Dashboard'; 

import api from '../../api';
import MonthlyReportSection from './MonthlyReportSection'; 
import SwipeRequestSection from './SwipeRequestSection'; 
import AttendanceApprovalsSection from './AttendanceApprovalsSection';

const statusStyles = {
  "PRESENT": { bg: "#4caf50", text: "#fff" }, 
  "PRESENT (GRACE)": { bg: "#729e09", text: "#fff" }, 
  "DLOP": { bg: "#d32f2f", text: "#fff" }, 
  "WO": { bg: "#607d8b", text: "#fff" }, 
  "HALF-DAY": { bg: "#ff9800", text: "#fff" }, 
  "UNALLOCATED": { bg: "#9e9e9e", text: "#fff" }, 
  "CL": { bg: "#2196f3", text: "#fff" }, 
  "ML": { bg: "#2196f3", text: "#fff" },
  "AL": { bg: "#2196f3", text: "#fff" },
  "NH": { bg: "#2196f3", text: "#fff" },
  "LOP": { bg: "#e91e63", text: "#fff" }, 
  "UPCOMING": { bg: "#f5f5f5", text: "#333" } 
};

const AttendanceDashboard = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const empId = localStorage.getItem('emp_id');
  const [activeMenu, setActiveMenu] = useState('my_own');
  
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsChange = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyReport, setDailyReport] = useState([]);

  const [myCorrections, setMyCorrections] = useState([]);

  const [team, setTeam] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState('');

  const [pendingReqs, setPendingReqs] = useState([]);
  const [historyReqs, setHistoryReqs] = useState([]);

  useEffect(() => {
    api.get(`/manager/${empId}/team`).then(res => setTeam(res.data.team)).catch(() => {});
    loadApprovals();
  }, [empId]);

  useEffect(() => {
    setPage(0);
    const month = selectedDate.getMonth() + 1;
    const year = selectedDate.getFullYear();
    const targetEmp = activeMenu === 'my_own' ? empId : selectedTeamMember;
    
    if (targetEmp && (activeMenu === 'my_own' || activeMenu === 'my_team')) {
      api.get(`/attendance/calendar/${targetEmp}?month=${month}&year=${year}`)
         .then(res => setCalendarData(res.data)).catch(console.error);
    }
    if (activeMenu === 'req_history') {
      api.get(`/attendance/my-corrections/${empId}`).then(res => setMyCorrections(res.data)).catch(console.error);
    }
    if (activeMenu === 'daily') {
      api.get(`/attendance/daily-report/${empId}?report_date=${reportDate}`).then(res => setDailyReport(res.data)).catch(console.error);
    }
  }, [selectedDate, activeMenu, selectedTeamMember, empId, reportDate]);

  const loadApprovals = async () => {
    try {
      const res = await api.get(`/attendance/approvals/${empId}`);
      setPendingReqs(res.data.pending);
      setHistoryReqs(res.data.history);
    } catch (err) { console.error(err); }
  };

  const changeMonth = (offset) => setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  const downloadProof = (reqId) => window.open(`http://${window.location.hostname}:8000/api/v1/attendance/correction/${reqId}/download`, '_blank');

  const renderCalendar = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay(); 
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const totalSlots = [...Array(firstDayOfMonth).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, bgcolor: '#f5f5f5', p: 1, borderRadius: 2 }}>
          <IconButton onClick={() => changeMonth(-1)}><ArrowBackIosIcon fontSize="small" /></IconButton>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>{monthNames[month]} {year}</Typography>
          <IconButton onClick={() => changeMonth(1)}><ArrowForwardIosIcon fontSize="small" /></IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Typography key={day} align="center" variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>{day}</Typography>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
          {totalSlots.map((dayNum, idx) => {
            if (!dayNum) return <Box key={`blank-${idx}`} sx={{ height: 40 }} />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            const dayData = calendarData.find(d => d.date === dateStr) || { status: 'UPCOMING', shift: 'Unknown', in_time: '--:--', out_time: '--:--', twh: '0h 0m', toh: '0h 0m', level: 'Level 1' };
            const style = statusStyles[dayData.status] || statusStyles["UPCOMING"];

            return (
              <Tooltip key={dayNum} title={
                  <Box sx={{ p: 1, minWidth: 150 }}>
                    <Typography variant="subtitle2" sx={{ borderBottom: '1px solid rgba(255,255,255,0.3)', pb: 0.5, mb: 0.5 }}>Date: {dateStr}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Status: {dayData.status}</Typography>
                    <Typography variant="body2">Shift: {dayData.shift}</Typography>
                    <Typography variant="body2">In: {dayData.in_time}</Typography>
                    <Typography variant="body2">Out: {dayData.out_time}</Typography>
                    
                    {(dayData.status !== 'UPCOMING' && dayData.status !== 'UNALLOCATED' && dayData.status !== 'WO') && (
                      <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5, color: '#ffeb3b' }}>
                        {dayData.level.includes('Level 1') ? `TWH: ${dayData.twh}` : `TOH: ${dayData.toh}`}
                      </Typography>
                    )}
                  </Box>
                } arrow placement="top">
                <Paper sx={{ 
                  height: 40, display: 'flex',  alignItems: 'center', justifyContent: 'center',
                  bgcolor: style.bg, color: style.text, fontWeight: 'bold', cursor: 'pointer',
                  borderRadius: '6px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.05)' }
                }}>{dayNum}</Paper>
              </Tooltip>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
          {Object.keys(statusStyles).map(key => (
            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: statusStyles[key].bg }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{key}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // UPDATED: NavItem with theme-based styling
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => setActiveMenu(id)}
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiListItemIcon-root': { color: 'white' },
          },
          '&.Mui-selected .MuiListItemText-primary': { 
            color: 'white',
            fontWeight: 'bold',
          },
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeMenu === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  // UPDATED: Section Header with theme-based styling
  const SectionHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - Matching Dashboard style */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <SectionHeader title="ATTENDANCE" />
          <NavItem id="my_own" label="My Own" icon={<CalendarMonthIcon fontSize="small" />} />
          <NavItem id="my_team" label="My Team" icon={<PeopleIcon fontSize="small" />} />
          <NavItem id="daily" label="Daily Report" icon={<AssessmentIcon fontSize="small" />} />
          <NavItem id="monthly" label="Monthly Report" icon={<DashboardIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="REQUESTS" />
          <NavItem id="swipe_req" label="Swipe Request" icon={<TouchAppIcon fontSize="small" />} />
          <NavItem id="req_history" label="Request History" icon={<HistoryIcon fontSize="small" />} />

          <Divider sx={{ my: dashboard.divider.marginY, mx: dashboard.divider.marginX }} />

          <SectionHeader title="APPROVALS" />
          <NavItem id="app_pending" label="Pending" icon={<HourglassEmptyIcon fontSize="small" />} />
          <NavItem id="app_history" label="History" icon={<FactCheckIcon fontSize="small" />} />
          
        </List>
      </Box>

      {/* RIGHT CONTENT AREA - Independent scrolling */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        width: '100%', 
        bgcolor: '#fbfbfb',
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        
        {activeMenu === 'my_own' && <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>My Attendance Calendar</Typography>}
        {activeMenu === 'my_team' && <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>Team Attendance</Typography>}
        {activeMenu === 'daily' && <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>Daily Team Report</Typography>}
        {activeMenu === 'swipe_req' && <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>Submit Swipe Request</Typography>}
        {activeMenu === 'req_history' && <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}>My Correction History</Typography>}

        {activeMenu === 'my_own' && <Paper elevation={2} sx={{ p: 4, maxWidth: 550, borderRadius: 2 }}>{renderCalendar()}</Paper>}

        {activeMenu === 'my_team' && (
          <Paper elevation={2} sx={{ p: 4, maxWidth: 550, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField select size="small" sx={{ minWidth: 250 }} label="Select Team Member" value={selectedTeamMember} onChange={e => setSelectedTeamMember(e.target.value)}>
                {team.map(t => <MenuItem key={t.emp_id} value={t.emp_id}>{t.first_name} {t.last_name}</MenuItem>)}
              </TextField>
            </Box>
            {selectedTeamMember ? renderCalendar() : <Alert severity="info" variant="outlined">Please select a team member to view their calendar.</Alert>}
          </Paper>
        )}

        {activeMenu === 'daily' && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField size="small" type="date" label="Report Date" InputLabelProps={{ shrink: true }} value={reportDate} onChange={e => setReportDate(e.target.value)} />
            </Box>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow><TableCell>Emp ID</TableCell><TableCell>Name</TableCell><TableCell>Status</TableCell><TableCell>Check-In</TableCell><TableCell>Check-Out</TableCell><TableCell>Total Hrs</TableCell><TableCell>Live Status</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {dailyReport.length === 0 ? <TableRow><TableCell colSpan={7} align="center">No members found.</TableCell></TableRow> :
                  dailyReport.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
                    <TableRow key={row.emp_id} hover>
                      <TableCell sx={{ fontWeight: 'bold' }}>{row.emp_id}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell><Chip size="small" label={row.day_status} color={row.day_status === 'PRESENT' ? 'success' : (row.day_status === 'DLOP' ? 'error' : 'default')} /></TableCell>
                      <TableCell>{row.check_in}</TableCell>
                      <TableCell>{row.check_out}</TableCell>
                      <TableCell>
                        <Tooltip title={`TWH: ${row.twh || '0h 0m'} | TOH: ${row.toh || '0h 0m'} (${row.level || 'Level 1'})`} placement="top" arrow>
                          <span style={{ borderBottom: '1px dashed #9e9e9e', cursor: 'help' }}>
                            {row.total_hrs}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {row.live_status !== "-" && <Chip size="small" label={row.live_status} color={row.live_status === 'IN' ? 'primary' : 'error'} variant="outlined" />}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination component="div" count={dailyReport.length} page={page} onPageChange={handlePageChange} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleRowsChange} rowsPerPageOptions={[5, 10, 25]} />
          </Paper>
        )}

        {/* MONTHLY REPORT RENDERED HERE */}
        {activeMenu === 'monthly' && <MonthlyReportSection />}

        {/* EXPORTED SWIPE REQUEST MODULE IS MOUNTED HERE */}
        {activeMenu === 'swipe_req' && (
          <SwipeRequestSection empId={empId} showMsg={showMsg} onSuccess={() => setActiveMenu('req_history')} />
        )}

        {activeMenu === 'req_history' && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow><TableCell>Date</TableCell><TableCell>Requested Times</TableCell><TableCell>Reason / Proof</TableCell><TableCell>Status</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {myCorrections.length === 0 ? <TableRow><TableCell colSpan={4} align="center">No history found.</TableCell></TableRow> :
                  myCorrections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(req => (
                    <TableRow key={req.id} hover>
                      <TableCell>{req.date}</TableCell>
                      <TableCell>{new Date(req.check_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} to {new Date(req.check_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</TableCell>
                      <TableCell>
                        {req.reason} <br/>
                        {req.attachment_path && <Button size="small" variant="outlined" sx={{ mt: 0.5 }} onClick={() => downloadProof(req.id)}>View File</Button>}
                      </TableCell>
                      <TableCell><Chip size="small" label={req.status} color={req.status.includes('APPROVED') ? 'success' : 'warning'} /></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination component="div" count={myCorrections.length} page={page} onPageChange={handlePageChange} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleRowsChange} rowsPerPageOptions={[5, 10, 25]} />
          </Paper>
        )}

        {/* ATTENDANCE APPROVALS MODULE MOUNTED HERE */}
        {(activeMenu === 'app_pending' || activeMenu === 'app_history') && (
          <AttendanceApprovalsSection 
            empId={empId} 
            activeMenu={activeMenu} 
            pendingReqs={pendingReqs} 
            historyReqs={historyReqs} 
            showMsg={showMsg} 
            refreshData={loadApprovals} 
          />
        )}
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%' }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>

    </Paper>
  );
};

export default AttendanceDashboard;